import React, { useState, useEffect, useCallback } from "react";
import {
  getIdrCases, saveIdrOverrides, generateIdr, downloadBlob, draftIdrSummary, reviewIdrCase,
} from "../../lib/api";
import { useMsal } from "@azure/msal-react";


const DRAFT_FIELDS = [
  { key: "alerts_documented", label: "Documented alerts / events" },
  { key: "interventions_summary", label: "Alerts & interventions summary" },
];

const READINESS = {
  green: { color: "#10B981", label: "Ready" },
  amber: { color: "#F59E0B", label: "Needs entry" },
  red: { color: "#EF4444", label: "Missing case data" },
};

function Badge({ kind }) {
  const map = {
    sql: { bg: "#E0F2FE", fg: "#075985", text: "SQL" },
    ai: { bg: "#FEF3C7", fg: "#92400E", text: "AI-extracted" },
    manual: { bg: "#F1F5F9", fg: "#475569", text: "Manual" },
  };
  const c = map[kind];
  return (
    <span style={{
      background: c.bg, color: c.fg, fontSize: 10, fontWeight: 600,
      letterSpacing: "0.03em", padding: "2px 6px", borderRadius: 4,
      textTransform: "uppercase", whiteSpace: "nowrap",
    }}>{c.text}</span>
  );
}

export default function IDRTab({ caseId, isAdmin }) {
  const { accounts } = useMsal();
  const account = accounts && accounts[0];
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [reviewNotes, setReviewNotes] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const preview = async () => {
    setGenerating(true);
    setError("");
    setNotice("");
    try {
      const { blob } = await generateIdr([caseId], draft, "Axis", true);
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const load = useCallback(() => {
    if (!caseId) { setLoading(false); return; }
    setLoading(true);
    setError("");
    getIdrCases()
      .then((data) => {
        const match = (data.cases || []).find((c) => String(c.patientId) === String(caseId));
        setCaseData(match || null);
        if (match && match.overrides && Object.keys(match.overrides).length) {
          setDraft((d) => ({ ...match.overrides, ...d }));
          if (match.reviewNotes && match.reviewNotes.length) setReviewNotes(match.reviewNotes);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const runDraft = async () => {
    setDrafting(true);
    setError("");
    try {
      const res = await draftIdrSummary(caseId);
      setDraft({ ...draft, ...res.draft });
      setReviewNotes(res.reviewNotes || []);
      setNotice("Draft generated. Review every line before saving.");
    } catch (e) {
      const msg = /429|rate.?limit|busy/i.test(e.message)
        ? "The AI service is busy right now. Wait a moment and try again."
        : e.message;
      setError(msg);
    } finally {
      setDrafting(false);
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    setError("");
    try {
      await saveIdrOverrides(caseId, draft, "");
      setNotice("Entries saved.");
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const markReviewed = async (reviewed) => {
    setSaving(true);
    setError("");
    try {
      const who = account?.idTokenClaims?.name || account?.username || "";
      await reviewIdrCase(caseId, reviewed, who);
      setNotice(reviewed ? "Marked reviewed. This case is now eligible for bulk download." : "Reopened for editing.");
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setError("");
    setNotice("");
    try {
      const { blob } = await generateIdr([caseId], draft, "Axis");
      downloadBlob(blob, caseId + "_IDR.pdf");
      setNotice("Downloaded " + filename);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const input = { width: "100%", padding: "7px 9px", fontSize: 13, borderRadius: 6,
    border: "1px solid #CBD5E1", background: "#fff", color: "#1B2A4A" };
  const empty = { padding: 32, textAlign: "center", color: "#64748B", fontSize: 13 };

  if (!isAdmin) {
    return <div style={empty}>IDR documentation requires the Admin role.</div>;
  }
  if (loading) {
    return <div style={empty}>Loading case...</div>;
  }
  if (!caseId) {
    return <div style={empty}>This document is not linked to a case, so no IDR document can be generated.</div>;
  }
  if (error && !caseData) {
    return <div style={empty}>{error}</div>;
  }
  if (!caseData) {
    return (
      <div style={empty}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: "#1B2A4A" }}>No IDR data for case {caseId}</div>
        <div>A History and Physical is required. Upload {caseId}_History_and_Physical.pdf, or check that the case has a scheduling record.</div>
      </div>
    );
  }

  const r = READINESS[caseData.readiness] || READINESS.red;
  const canDownload =
    caseData.reviewStatus === "reviewed" &&
    caseData.hasNarrative &&
    caseData.readiness === "green";
  const gateHint = !canDownload
    ? (caseData.reviewStatus !== "reviewed" ? "Mark the case reviewed to enable download."
      : !caseData.hasNarrative ? "Generate and save a narrative to enable download."
      : "Case is missing required data.")
    : "";

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>IDR Supporting Documentation</div>
          <div style={{ fontSize: 12.5, color: "#64748B" }}>Case {caseData.patientId}</div>
        </div>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: r.color }} />
          {r.label}
        </span>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 12, borderLeft: "3px solid #EF4444",
          background: "#FEF2F2", fontSize: 13, borderRadius: 6 }}>{error}</div>
      )}
      {notice && (
        <div style={{ padding: 12, marginBottom: 12, borderLeft: "3px solid #10B981",
          background: "#F0FDF4", fontSize: 13, borderRadius: 6 }}>{notice}</div>
      )}

      {/* Sourced values */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", letterSpacing: "0.04em",
          textTransform: "uppercase", marginBottom: 10 }}>From the portal</div>
        {[
          ["Service date", caseData.serviceDate, "sql"],
          ["Monitoring duration", caseData.monitoringDuration, "sql"],
          ["Facility", caseData.facility, "sql"],
          ["Procedure", caseData.procedure, "sql"],
          ["Modalities", caseData.modalities, "ai"],
          ["Interpreting physician", caseData.readerName, "sql"],
          ["Technologist", caseData.techName, "sql"],
        ].map((row) => (
          <div key={row[0]} style={{ display: "flex", justifyContent: "space-between",
            alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
            <div style={{ fontSize: 12.5, color: "#64748B" }}>{row[0]}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13 }}>{row[1] || "-"}</span>
              <Badge kind={row[2]} />
            </div>
          </div>
        ))}
      </div>

      {/* Narrative + complexity */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", letterSpacing: "0.04em",
            textTransform: "uppercase", display: "flex", gap: 8, alignItems: "center" }}>
            Summary of Services <Badge kind="ai" />
          </div>
          <button className="btn" disabled={drafting} onClick={runDraft}>
            {drafting ? "Drafting..." : "Draft from reports"}
          </button>
        </div>

        {DRAFT_FIELDS.map((f) => (
          <div key={f.key} style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>{f.label}</label>
            <input style={input} value={draft[f.key] || ""}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
          </div>
        ))}

        <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>Narrative</label>
        <textarea
          style={{ ...input, minHeight: 160, resize: "vertical", fontFamily: "inherit", lineHeight: 1.55 }}
          value={draft.case_summary || ""}
          onChange={(e) => setDraft({ ...draft, case_summary: e.target.value })}
          placeholder="Draft from reports, then edit. Blank until you save."
        />
        <p style={{ fontSize: 11.5, color: "#92400E", background: "#FEF3C7", padding: "8px 10px",
          borderRadius: 6, marginTop: 8, marginBottom: 0 }}>
          Drafted by AI from the case documents. Read every sentence against the
          source before saving — this text goes to a payer as evidence.
        </p>

        {reviewNotes.length > 0 && (
          <div style={{ marginTop: 10, padding: "10px 12px", background: "#FEF2F2",
            border: "1px solid #FECACA", borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#B91C1C", letterSpacing: "0.04em",
              textTransform: "uppercase", marginBottom: 6 }}>
              Omitted for missing information ({reviewNotes.length})
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#7F1D1D", lineHeight: 1.6 }}>
              {reviewNotes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
            <div style={{ fontSize: 11.5, color: "#7F1D1D", marginTop: 8 }}>
              These were left out rather than guessed at. Add them manually if the letter needs them.
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 12, borderTop: "1px solid #E2E8F0", flexWrap: "wrap" }}>
        <button className="btn" disabled={saving} onClick={saveDraft}>
          {saving ? "Saving..." : "Save entries"}
        </button>
        <button className="btn" disabled={generating} onClick={preview}>
          {generating ? "Building..." : "Preview"}
        </button>
        <button className="btn btn-primary" disabled={generating || !canDownload}
          onClick={generate} title={gateHint}>
          {generating ? "Generating..." : "Download PDF"}
        </button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {!canDownload && gateHint && (
            <span style={{ fontSize: 11.5, color: "#94A3B8" }}>{gateHint}</span>
          )}
          {caseData.reviewStatus === "reviewed" ? (
            <>
              <span style={{ fontSize: 12, color: "#10B981", fontWeight: 600 }}>
                ✓ Reviewed{caseData.reviewedBy ? " · " + caseData.reviewedBy : ""}
              </span>
              <button className="btn" disabled={saving} onClick={() => markReviewed(false)}>Reopen</button>
            </>
          ) : (
            <button className="btn btn-primary" disabled={saving} onClick={() => markReviewed(true)}>
              Mark reviewed
            </button>
          )}
        </div>
      </div>

      {previewUrl && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(15,27,46,0.55)", zIndex: 60,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }}
        >
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 10, width: "min(900px, 100%)", height: "90vh",
              display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 16px", borderBottom: "1px solid #E2E8F0" }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Preview — case {caseId}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-primary" onClick={generate} disabled={!canDownload} title={gateHint}>Download</button>
                <button className="btn" onClick={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }}>
                  Close
                </button>
              </div>
            </div>
            <iframe title="IDR preview" src={previewUrl} style={{ flex: 1, border: "none" }} />
          </div>
        </div>
      )}
    </div>
  );
}